import React, { useMemo, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  Footer,
  FooterBottomMenu,
  LayoutSingleColumn,
  LayoutWrapperFooter,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  LeftBar,
  Modal,
  NamedLink,
  Page,
} from 'components';
import TopbarContainer from 'containers/TopbarContainer/TopbarContainer';
import { ShareForm } from 'forms';
import { compose } from 'redux';
import { withViewport } from 'util/contextHelpers';
import config from 'config';
import { IMAGES } from 'util/imagesPath';
import { isScrollingDisabled } from 'ducks/UI.duck';
import { data as siteData, getBlogDetails } from 'containers/BlogPage/BlogPage.data';
import classNames from 'classnames';
import css from 'containers/BlogPage/ShowBlogPage/ShowBlog.css';
import { Helmet } from 'react-helmet-async';

const ShowBlogPage = ({ isTennisSite, isEducationSite, viewport, params, intl, ...props }) => {
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));

  const [isOpenShareForm, setIsOpenShareForm] = useState(false);
  const [activeFooterTab, setActiveFooterTab] = useState('');
  const [showBlog, setShowBlog] = useState(null);

  const viewportWidth = viewport.width;
  const MAX_MOBILE_SCREEN_WIDTH = 1024;
  const isMobileLayout = useMemo(
    () => (!!viewportWidth ? viewportWidth < MAX_MOBILE_SCREEN_WIDTH : false),
    [viewportWidth]
  );

  const schemaTitle = intl.formatMessage(
    { id: isEducationSite ? 'EducationBlogPage.schemaTitle' : 'BlogPage.schemaTitle' },
    { siteTitle: config?.siteTitle }
  );
  const schemaDescription = intl.formatMessage({
    id: isEducationSite ? 'Page.schemaDescription' : 'HomePage.schemaDescription',
  });
  const schemaImage = `${config.canonicalRootURL}${IMAGES.facebookImage}`;

  const url = typeof window !== 'undefined' && window.location.href;

  const blogData = getBlogDetails(params?.blogSlug);

  const renderBlogContent = (dt, wrapperClass) => {
    const [key, val] = Object.entries(dt)[0];
    if (wrapperClass === 'summaryWrap') {
      switch (key) {
        case 'span':
          return <span className={css.summaryNote}>{val}</span>;
        case 'p':
          return <p className={css.summaryText}> {val} </p>;
        default:
          break;
      }
    }
    if (wrapperClass === 'FullWidthText') {
      switch (key) {
        case 'p':
          return <p className={css.paragraphText}> {val} </p>;
        case 'img':
          return <img {...val} />;
        case 'h3':
          return <h3 className={css.blogDetailHead}> {val} </h3>;
        default:
          break;
      }
    } else {
      switch (key) {
        case 'img':
          return <img {...val} />;
        case 'h3':
          return <h3 className={css.blogDetailHead}> {val} </h3>;
        case 'p':
          return <p className={css.blogDetailText}> {val} </p>;
        case 'span':
          return <span className={css.summaryNote}>{val}</span>;
        default:
          break;
      }
    }
  };

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      isBottomMenuRequire={false}
      contentType="website"
      description={schemaDescription}
      title={`${schemaTitle} | ${blogData !== undefined ? blogData?.title : ''}`}
      facebookImages={[{ url: IMAGES.facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        {
          url: `${config.canonicalRootURL}${IMAGES.twitterImage}`,
          width: 600,
          height: 314,
        },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: `${schemaTitle} | ${blogData !== undefined ? blogData?.title : ''}`,
        image: [schemaImage],
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer
            setIsOpenShareForm={setIsOpenShareForm}
            isEducationSite={isEducationSite}
            // isTennisSite={isTennisSite}
          />
        </LayoutWrapperTopbar>

        <LayoutWrapperMain>
          <div className={classNames(css.main)}>
            {!isMobileLayout && (
              <LeftBar
                history={props.history}
                // onShowEnquiryTab={onSearch}
                onClose={() => setActiveFooterTab('search')}
                isEducationSite={isEducationSite}
              />
            )}
            <div className={css.cardContainer}>
              <div className={css.breadCrumbWrapper}>
                <NamedLink name={isEducationSite ? 'EducationPage' : 'HomePage'}>Home</NamedLink>
                <span className={css.breadcrumbArrow}>&gt;</span>
                <NamedLink
                  name={isEducationSite ? 'EducationBlogPage' : 'BlogPage'}
                  className={css.currentListingTypeName}
                >
                  Blogs
                </NamedLink>
                <span className={css.breadcrumbArrow}>&gt;</span>
                <NamedLink
                  name={isEducationSite ? 'EduCategoryBlogPage' : 'CategoryBlogPage'}
                  params={{
                    slug: params?.slug,
                  }}
                  className={css.currentListingTypeName}
                >
                  {params?.slug}
                </NamedLink>
                <span className={css.breadcrumbArrow}>&gt;</span>
                <NamedLink
                  name={isEducationSite ? 'EduCategoryBlogPage' : 'CategoryBlogPage'}
                  params={{
                    slug: params?.slug,
                    blogSlug: params?.blogSlug,
                  }}
                  className={css.currentListingTypeName}
                >
                  {params?.blogSlug}
                </NamedLink>
              </div>
              {blogData === undefined ? (
                <p>No Blogs Found</p>
              ) : (
                <div className={css.blogMain}>
                  <img src={blogData?.heroImgUrl} />
                  <h2 className={css.blogHead}>{blogData?.title}</h2>
                  <h3 className={css.blogText}>{blogData?.subTitle}</h3>

                  <div className={css.blogWrap}>
                    {blogData?.content?.map((cont, ind) => (
                      <div className={css[cont?.className]} key={`${cont?.className} ${ind}`}>
                        {cont.data.map(dt => renderBlogContent(dt, cont?.className))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className={css.subscribeWrap}>
                <span className={css.subscribeText}>
                  Receive our newest blog posts directly in your email inbox.
                </span>
                <button className={css.subscribeButtton}>Subscribe to our email list</button>
              </div>
            </div>
          </div>
        </LayoutWrapperMain>
        {!isMobileLayout ? (
          <LayoutWrapperFooter>
            <Footer isEducationSite={isEducationSite} />
          </LayoutWrapperFooter>
        ) : (
          <LayoutWrapperFooter
            className={classNames(css.layoutFooter, {
              [css.activeleftbarFooter]: activeFooterTab === 'leftbar',
              [css.createFooterTab]:
                activeFooterTab === 'create' || activeFooterTab === 'activitySearch',
            })}
          >
            {!!isMobileLayout && !(activeFooterTab === 'leftbar') && (
              <FooterBottomMenu
                activeFooterTab={activeFooterTab}
                changeActivieFooterTab={value => setActiveFooterTab(value)} // setState({ activeFooterTab: value })}
                // handleProviderQueryChange={handleProviderQueryChange}
                history={props.history}
                // onSearch={activity => onkeywordSearch(activity)}
                isEducationSite={isEducationSite}
              />
            )}
          </LayoutWrapperFooter>
        )}
      </LayoutSingleColumn>
      <Modal
        isOpen={!!(activeFooterTab === 'leftbar')}
        onClose={() => setActiveFooterTab('search')}
        id="filterModal"
        onManageDisableScrolling={() => {}}
        className={css.modalLeftBar}
      >
        <LeftBar
          isMobileLayout={isMobileLayout}
          history={props.history}
          onShowEnquiryTab={() => {}}
          onLogoClick={() => setActiveFooterTab('search')}
          onClose={() => setActiveFooterTab('search')}
          isEducationSite={isEducationSite}
        />
      </Modal>
      <Modal
        id="AllSportsPage.shareForm"
        className={css.shareFormModal}
        isOpen={isOpenShareForm}
        onClose={() => setIsOpenShareForm(false)}
        onManageDisableScrolling={() => {}}
      >
        <ShareForm url={url} />
      </Modal>
      <Modal
        id="categoryblogs"
        className={css.blogModal}
        isOpen={showBlog !== null}
        onClose={() => setShowBlog(null)}
        onManageDisableScrolling={() => {}}
        containerClassName={css.blogModalContainer}
        // contentClassName={css.blogContent}
      >
        <>
          {siteData[params?.slug]?.children[showBlog]?.blogUrl?.map((url, i) => (
            <img
              src={url}
              alt={`${siteData[params?.slug]?.children[showBlog]?.title}_${i}`}
              width="auto"
              height="auto"
            />
          ))}
        </>
      </Modal>
    </Page>
  );
};

export default compose(withViewport, injectIntl)(ShowBlogPage);
